/**
 * iOS WebKit Navigation Handler
 *
 * Provides utilities for intercepting navigation actions in iOS WKWebView.
 * Used by the native iOS app's `decidePolicyFor navigationAction` delegate method.
 */

import { useLocation } from "@tanstack/react-router";
import { useEffect } from "react";
import { captureException } from "@/utils/error-tracking";
import { useWebViewIntegration } from "./url-state";

/**
 * Check if iOS WebKit message handlers are available
 * Only returns true if:
 * 1. Running on iOS device (iPhone/iPad/iPod)
 * 2. In a WebView (not Safari)
 * 3. webkit.messageHandlers exists
 * 4. ReactNativeWebView is NOT present (to avoid conflicts)
 */
function isIOSWebKitAvailable(): boolean {
	if (typeof window === "undefined") return false;

	// Check if ReactNativeWebView exists first - if it does, use that instead
	// biome-ignore lint/suspicious/noExplicitAny: ReactNativeWebView is dynamically injected
	if ((window as any).ReactNativeWebView) {
		return false; // Prefer ReactNativeWebView over native iOS handlers
	}

	const userAgent = navigator.userAgent || "";
	const isIOSDevice = /iPhone|iPad|iPod/.test(userAgent);
	const isWebView = /WebView/.test(userAgent);
	const hasWebKitHandlers =
		typeof window.webkit !== "undefined" &&
		typeof window.webkit.messageHandlers !== "undefined";

	return isIOSDevice && isWebView && hasWebKitHandlers;
}

export interface NavigationActionData {
	url: string;
	navigationType: "link" | "form" | "programmatic" | "back";
	target?: string;
	method?: string;
	metadata?: Record<string, unknown>;
}

/**
 * Intercept navigation action and notify iOS native app
 * Dispatches custom event and sends message via webkit handler if available
 */
export function interceptNavigation(
	url: string,
	navigationType: NavigationActionData["navigationType"],
	metadata?: Record<string, unknown>,
): void {
	const actionData: NavigationActionData = {
		url,
		navigationType,
		metadata,
	};

	// Dispatch custom event for iOS to intercept
	try {
		window.dispatchEvent(
			new CustomEvent("ios-navigation-action", { detail: actionData }),
		);
	} catch (error) {
		captureException(error, {
			tags: {
				component: "ios-navigation-handler",
				action: "dispatchNavigationEvent",
			},
		});
		if (process.env.NODE_ENV === "development") {
			console.warn(
				"[Navigation] Failed to dispatch navigation action event:",
				error,
			);
		}
	}

	// Also send via webkit message handler if available (only if ReactNativeWebView not present)
	if (
		isIOSWebKitAvailable() &&
		window.webkit?.messageHandlers?.navigationHandler
	) {
		try {
			window.webkit.messageHandlers.navigationHandler.postMessage(
				JSON.stringify(actionData),
			);
		} catch (error) {
			captureException(error, {
				tags: {
					component: "ios-navigation-handler",
					action: "postMessageNavigationHandler",
				},
			});
			if (process.env.NODE_ENV === "development") {
				console.warn(
					"[Navigation] Failed to send navigation action to iOS:",
					error,
				);
			}
		}
	}
}

/**
 * Hook to intercept link clicks (OPTIONAL - only enable if iOS app needs navigation interception)
 * This should be opt-in to avoid interfering with normal navigation
 *
 * @param enabled - Whether to enable navigation interception (default: false)
 */
export function useIOSNavigationHandler(enabled: boolean = false): void {
	useEffect(() => {
		// Only enable if explicitly requested AND iOS WebKit is available
		if (!enabled || !isIOSWebKitAvailable()) return;

		const handleLinkClick = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			const link = target.closest("a");

			if (link?.href) {
				// Only intercept external links or links with specific data attributes
				// Don't intercept internal navigation - let TanStack Router handle it
				const isExternal =
					link.href.startsWith("http") &&
					!link.href.startsWith(window.location.origin);
				const shouldIntercept =
					isExternal || link.dataset.iosIntercept === "true";

				if (shouldIntercept) {
					interceptNavigation(link.href, "link", {
						target: link.target,
						text: link.textContent || undefined,
					});
				}
			}
		};

		document.addEventListener("click", handleLinkClick, true);
		return () => {
			document.removeEventListener("click", handleLinkClick, true);
		};
	}, [enabled]);
}

/**
 * Check if React Native WebView is available
 */
function isReactNativeWebView(): boolean {
	if (typeof window === "undefined") return false;
	// biome-ignore lint/suspicious/noExplicitAny: ReactNativeWebView is dynamically injected
	return typeof (window as any).ReactNativeWebView !== "undefined";
}

/**
 * Hook to intercept TanStack Router navigation and send events to WebView
 * Automatically sends navigation events when route changes
 */
export function useIOSRouterIntegration(): void {
	const location = useLocation();
	const { sendToWebView } = useWebViewIntegration();

	useEffect(() => {
		// Only send navigation events if WebView is available
		// (useWebViewIntegration already handles ReactNativeWebView internally)
		if (!isIOSWebKitAvailable() && !isReactNativeWebView()) return;

		// Send navigation events to WebView (handles both iOS and React Native)
		sendToWebView("NAVIGATION", {
			path: location.pathname,
			search: location.search,
			hash: location.hash,
		});
	}, [location.pathname, location.search, location.hash, sendToWebView]);
}
